import { useCallback } from 'react';

import {
  GlobalCompanyTabName,
  GlobalCompanyTabRoleUID,
  GlobalCompanyTabUUID
} from '../../../../../globalCompanyTabsTypes';

import { useReactHookForm } from '../../../../../../common/hooks/useReactHookForm';

import { useUpdateGlobalCompanyTab } from '../../../../../hooks/useUpdateGlobalCompanyTab';
import {
  UPDATE_GLOBAL_COMPANY_TAB_QUERY,
  UpdateGlobalCompanyTabQueryResponse
} from '../../../../../queries/updateGlobalCompanyTab.query';

import { GlobalCompanyTabsCache } from '../../../../../GlobalCompanyTabsCache';

export interface UpdateGlobalCompanyTabFormData {
  name: GlobalCompanyTabName;
  roleIds: GlobalCompanyTabRoleUID[];
}

interface UseUpdateGlobalCompanyTabFormProps {
  globalCompanyTabUuid: GlobalCompanyTabUUID;
  name: GlobalCompanyTabName;
  roleIds: GlobalCompanyTabRoleUID[];
  hideModal: () => void;
}

function useUpdateGlobalCompanyTabForm({
  globalCompanyTabUuid,
  name,
  roleIds,
  hideModal
}: UseUpdateGlobalCompanyTabFormProps) {
  const { control, errors, handleSubmitReactHookForm, register } =
    useReactHookForm<UpdateGlobalCompanyTabFormData>({
      defaultValues: { name, roleIds },
      isModalForm: true
    });

  const {
    updateGlobalCompanyTab,
    updateGlobalCompanyTabLoading,
    updateGlobalCompanyTabErrorMessage
  } = useUpdateGlobalCompanyTab<UpdateGlobalCompanyTabQueryResponse>({
    query: UPDATE_GLOBAL_COMPANY_TAB_QUERY,
    cacheKeys: [GlobalCompanyTabsCache.indexCacheKey()]
  });

  const registerName = register('name', { required: true });

  const handleSubmit = useCallback<() => void>(() => {
    handleSubmitReactHookForm({
      onSubmit: async (data: UpdateGlobalCompanyTabFormData) =>
        updateGlobalCompanyTab({
          uid: globalCompanyTabUuid,
          name: data.name.trim(),
          roleUids: data.roleIds
        }).then((response) => {
          if (response?.updateGlobalCompanyTab?.status === 'success') {
            hideModal();
          }
        }),
      dirtyFieldsOnly: false
    })();
  }, [
    handleSubmitReactHookForm,
    updateGlobalCompanyTab,
    globalCompanyTabUuid,
    hideModal
  ]);

  return {
    control,
    errors,
    registerName,
    handleSubmit,
    updateGlobalCompanyTabLoading,
    updateGlobalCompanyTabErrorMessage
  };
}

export default useUpdateGlobalCompanyTabForm;
